import { motion } from "framer-motion";

/**
 * StorySection — the NOOR brand-story poem, reusable across surfaces:
 *
 *   "For five years, we chased a forgotten light…"
 *
 * Renders three stanzas:
 *   1. The 6-line origin poem (italic, vellum)
 *   2. The declarative bridge "This is NOOR, a spirit of eternal light." (bone)
 *   3. The closing signature "Born from Light. / Crafted for Legends." (rose-gold)
 *
 * A hairline rose-gold rule separates stanzas 1 and 2 for editorial cadence.
 *
 * Props:
 *   align      — "center" (default) or "left"
 *   className  — extra classes appended to the section element
 *   testId     — overrides the root data-testid (default `story-section`)
 */

const EASE = [0.22, 1, 0.36, 1];
const FADE_INIT = { opacity: 0, y: 24 };
const FADE_ANIM = { opacity: 1, y: 0 };
const FADE_VIEWPORT = { once: true, amount: 0.4 };
const FADE_TRANS = { duration: 1, delay: 0.2, ease: EASE };

const STANZA_QUEST = [
  "For five years, we chased a forgotten light",
  "through shadowed halls and ancient gardens.",
  "In its glow, we found rose and vanilla",
  "and gave them a throne beside agave.",
  "A kingdom in every pour,",
  "where taste meets legacy.",
];
const STANZA_DECLARATION = ["This is NOOR, a spirit of eternal light."];
const STANZA_SIGNATURE = ["Born from Light.", "Crafted for Legends."];

const STYLE_QUEST = {
  color: "var(--noor-vellum)",
  fontSize: "clamp(1.05rem, 1.55vw, 1.3rem)",
  lineHeight: 1.85,
};
const STYLE_DECLARATION = {
  color: "var(--noor-bone)",
  fontSize: "clamp(1.15rem, 1.7vw, 1.4rem)",
  lineHeight: 1.6,
};
const STYLE_SIGNATURE = {
  color: "var(--accent-rose)",
  fontSize: "clamp(1.05rem, 1.55vw, 1.3rem)",
  letterSpacing: "0.02em",
  lineHeight: 1.65,
};
const STYLE_RULE = {
  background: "var(--noor-rosegold)",
  opacity: 0.45,
  width: 64,
  height: 1,
};

function Stanza({ lines, style, italic = true, testId }) {
  return (
    <p
      data-testid={testId}
      className={`font-display ${italic ? "italic" : ""}`}
      style={style}
    >
      {lines.map((line, i) => (
        <span key={`${line.slice(0, 12)}-${i}`}>
          {line}
          {i < lines.length - 1 && <br />}
        </span>
      ))}
    </p>
  );
}

export default function StorySection({ align = "center", className = "", testId = "story-section" }) {
  const alignmentClass = align === "left" ? "items-start text-left" : "items-center text-center";
  return (
    <motion.section
      data-testid={testId}
      initial={FADE_INIT}
      whileInView={FADE_ANIM}
      viewport={FADE_VIEWPORT}
      transition={FADE_TRANS}
      className={`max-w-[58ch] mx-auto flex flex-col gap-8 ${alignmentClass} ${className}`.trim()}
    >
      <Stanza lines={STANZA_QUEST} style={STYLE_QUEST} testId="story-quest" />
      <span aria-hidden className="block" style={STYLE_RULE} />
      <Stanza
        lines={STANZA_DECLARATION}
        style={STYLE_DECLARATION}
        italic={false}
        testId="story-declaration"
      />
      <Stanza lines={STANZA_SIGNATURE} style={STYLE_SIGNATURE} testId="story-signature" />
    </motion.section>
  );
}
